import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';

const AuthContext = createContext(null);

const FREE_PLAN_LIMITS = {
    maxDomains: 1,
    maxPagesPerMonth: 100,
    allowScheduling: false,
    allowPdfExport: false,
    maxJourneys: 0,
    historyRetentionDays: 7,
    maxLocalAgents: 1,
    allowSharing: false
};

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [claims, setClaims] = useState(null);
    const [userRole, setUserRole] = useState('free');
    const [planLimits, setPlanLimits] = useState(FREE_PLAN_LIMITS);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let unsubProfile = null;

        const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
            setUser(firebaseUser);
            if (firebaseUser) {
                // Fetch custom claims from the ID token
                try {
                    const tokenResult = await firebaseUser.getIdTokenResult();
                    const currentClaims = tokenResult.claims;
                    setClaims(currentClaims);
                    
                    if (currentClaims?.admin || currentClaims?.role === 'admin') {
                        setUserRole('admin');
                    } else if (currentClaims?.role) {
                        setUserRole(currentClaims.role);
                    } else {
                        setUserRole('free');
                    }
                    
                    if (currentClaims?.planLimits) {
                        setPlanLimits(currentClaims.planLimits);
                    } else {
                        setPlanLimits(FREE_PLAN_LIMITS);
                    }
                } catch (err) {
                    console.error('Error fetching claims:', err);
                    setClaims(null);
                    setUserRole('free');
                    setPlanLimits(FREE_PLAN_LIMITS);
                }

                // Clean up any existing listener before starting a new one
                if (unsubProfile) {
                    unsubProfile();
                }

                // Listen to the user's profile in real-time
                const profileRef = doc(db, 'users', firebaseUser.uid);
                unsubProfile = onSnapshot(profileRef, (snap) => {
                    if (snap.exists()) {
                        const profileData = snap.data();
                        setProfile(profileData);
                        // Keep feature gates current when billing/admin updates the root profile.
                        if (profileData.planLimits) setPlanLimits({ ...FREE_PLAN_LIMITS, ...profileData.planLimits });
                        if (profileData.role) setUserRole(profileData.role);
                    } else {
                        setProfile(null);
                    }
                    setLoading(false);
                }, (err) => {
                    console.error('Error listening to profile:', err);
                    setProfile(null);
                    setLoading(false);
                });

            } else {
                if (unsubProfile) {
                    unsubProfile();
                    unsubProfile = null;
                }
                setProfile(null);
                setClaims(null);
                setUserRole('free');
                setPlanLimits(FREE_PLAN_LIMITS);
                setLoading(false);
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubProfile) {
                unsubProfile();
            }
        };
    }, []);

    /**
     * Force refresh the user's ID token to pick up new custom claims
     * (e.g., after the Cloud Function sets them).
     */
    const refreshClaims = async () => {
        if (user) {
            try {
                const tokenResult = await user.getIdTokenResult(true); // force refresh
                const currentClaims = tokenResult.claims;
                setClaims(currentClaims);
                
                if (currentClaims?.admin || currentClaims?.role === 'admin') {
                    setUserRole('admin');
                } else if (currentClaims?.role) {
                    setUserRole(currentClaims.role);
                } else {
                    setUserRole('free');
                }

                if (currentClaims?.planLimits) {
                    setPlanLimits(currentClaims.planLimits);
                } else {
                    setPlanLimits(FREE_PLAN_LIMITS);
                }

                return currentClaims;
            } catch (err) {
                console.error('Error refreshing claims:', err);
            }
        }
        return null;
    };

    return (
        <AuthContext.Provider value={{ user, profile, setProfile, claims, userRole, planLimits, refreshClaims, loading }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
